'use client';

import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCentrifugo } from '../contexts/CentrifugoContext';
import { queryKeys } from '../lib/queryClient';

const getCaseBattlesChannel = (prefix: string): string => {
  // Static channel for all case battles (we filter by battleId in the handler)
  return prefix ? `${prefix}_case_battles` : 'case_battles';
};

export type CaseBattleEventType =
  | 'battle_created'
  | 'player_joined'
  | 'player_left'
  | 'battle_completed';

export interface CaseBattleEvent {
  type: CaseBattleEventType;
  data: {
    battleId?: string;
    roundNumber?: number;
    winnerId?: string;
    isTie?: boolean;
    userId?: string;
    slot?: number;
    results?: any[];
    totalRounds?: number;
    [key: string]: unknown;
  };
}

interface UseCaseBattleSubscriptionOptions {
  onEvent?: (event: CaseBattleEvent) => void;
}

export const useCaseBattleSubscription = (
  battleId: string | undefined,
  options?: UseCaseBattleSubscriptionOptions
) => {
  const { subscribe, isConnected, channelPrefix } = useCentrifugo();
  const queryClient = useQueryClient();
  const onEventRef = useRef(options?.onEvent);

  // Keep ref updated
  useEffect(() => {
    onEventRef.current = options?.onEvent;
  }, [options?.onEvent]);

  const channelName = useMemo(() => {
    // Subscribe to static channel for all battles
    return getCaseBattlesChannel(channelPrefix);
  }, [channelPrefix]);

  const handleEvent = useCallback(
    (data: unknown) => {
      try {
        // Log raw received event
        console.log('[WS] Raw event received:', data);

        const event = data as CaseBattleEvent;

        // Safe check for event data
        if (!event || !event.data) {
          console.warn('[CaseBattle] Received malformed event:', data);
          return;
        }

        // Log parsed event details
        console.log('[WS] Parsed event:', {
          type: event.type,
          battleId: event.data.battleId,
          userId: event.data.userId,
          slot: event.data.slot,
        });

        // Filter: only process events for our battleId (or all events if no battleId specified)
        if (battleId && event.data.battleId !== battleId) {
          return;
        }

        // Call the event callback if provided
        if (onEventRef.current) {
          onEventRef.current(event);
        }

        // Directly mutate cache based on event type
        console.log(`[WS] Processing event type: ${event.type}`);
        
        switch (event.type) {
          case 'battle_created':
            console.log('[WS] Invalidating queries for battle_created...');
            // Update battles list when a new battle is created
            queryClient.invalidateQueries({ queryKey: queryKeys.caseBattles });
            queryClient.invalidateQueries({ queryKey: queryKeys.caseBattlesCount });
            console.log('[WS] ✅ Completed invalidation for battle_created');
            break;

          case 'player_joined':
            console.log('[WS] Invalidating queries for player_joined...');
            // Update battle details with new player
            if (battleId) {
              queryClient.setQueryData(queryKeys.caseBattleDetails(battleId), (old: any) => {
                if (!old) return old;
                // Server will send full updated data, just refetch
                return old;
              });
              // Refetch to get updated participant list
              queryClient.invalidateQueries({ queryKey: queryKeys.caseBattleDetails(battleId) });
            }
            // Update battles list
            queryClient.invalidateQueries({ queryKey: queryKeys.caseBattles });
            queryClient.invalidateQueries({ queryKey: queryKeys.caseBattlesCount });
            console.log('[WS] ✅ Completed invalidation for player_joined');
            break;

          case 'player_left':
            console.log('[WS] Invalidating queries for player_left...');
            if (battleId) {
              queryClient.invalidateQueries({ queryKey: queryKeys.caseBattleDetails(battleId) });
            }
            queryClient.invalidateQueries({ queryKey: queryKeys.caseBattles });
            queryClient.invalidateQueries({ queryKey: queryKeys.caseBattlesCount });
            console.log('[WS] ✅ Completed invalidation for player_left');
            break;

          case 'battle_completed':
            console.log('[WS] Battle completed, prefetching details...');
            // Prefetch battle details to get complete results before animation
            if (battleId) {
              queryClient.prefetchQuery({
                queryKey: queryKeys.caseBattleDetails(battleId),
              });
            }
            // Refetch battle details to get complete results and animate locally
            if (battleId) {
              queryClient.invalidateQueries({ queryKey: queryKeys.caseBattleDetails(battleId) });
            }
            // Update other queries
            queryClient.invalidateQueries({ queryKey: queryKeys.caseBattles });
            queryClient.invalidateQueries({ queryKey: queryKeys.caseBattlesCount });
            queryClient.invalidateQueries({ queryKey: queryKeys.profile });
            // Fix: Use predicate to match all history queries regardless of limit parameter
            queryClient.invalidateQueries({
              predicate: (query) => {
                const key = query.queryKey;
                return Array.isArray(key) && key[0] === 'caseBattles' && key[1] === 'history';
              }
            });
            console.log('[WS] ✅ Completed invalidation for battle_completed');
            break;
        }
      } catch (error) {
        console.error('[WS] ❌ Error handling event:', error);
        console.error('[WS] Event data that caused error:', data);
      }
    },
    [battleId, queryClient]
  );

  useEffect(() => {
    if (!isConnected || !channelName) return;

    const unsubscribe = subscribe(channelName, handleEvent);

    return unsubscribe;
  }, [isConnected, channelName, subscribe, handleEvent]);

  return { isConnected };
};
